import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React from 'react';
import { Button } from '../../flayr-components';
import { Heading } from '../../flayr-components/Heading/Heading';
import { ShowIf } from '../../flayr-components/ShowIf/ShowIf';
import useToggle from '../../hooks/useToggle';
import Drawer from '../../lib/Drawer/Drawer';
import FormWizard from '../FormWizard/FormWizard';

interface IPricingInclusionsProps {
  total: number;
  deposit: number;
  credits?: number;
  prevDepositUsed?: number;
  amountAfterCredits?: number;
  totalPostCredits?: number;
  remainder?: number;
  inclusions: string[];
  bonus?: string;
  currency?: string;
  placeholder?: boolean;
}

const PricingInclusions: React.FC<React.PropsWithChildren<React.PropsWithChildren<IPricingInclusionsProps>>> = ({
  total,
  deposit,
  inclusions,
  remainder,
  credits,
  bonus,
  currency = '$',
  amountAfterCredits,
  totalPostCredits,
  prevDepositUsed,
  placeholder = false,
}) => {
  const inclusionCollapse = useToggle();
  const drawer = useToggle();

  // dear future dev, sorry this component is a mess.
  // initially, there were a lot of the changes in terms of data structure and information to display so I had the get this working somehow
  // I wasn't able to get back and clean this component.

  return (
    <div className="text-sm">
      <Heading bold="medium" className="lg:text-4xl lg:mb-12">
        Pricing & Inclusions
      </Heading>
      <div hidden={placeholder}>
        <div className="text-2xl font-medium lg:text-3xl">
          {!totalPostCredits && (
            <span className="inline-block my-2">
              {currency}
              {total?.toLocaleString()}
            </span>
          )}
          {!!totalPostCredits && (
            <span className="inline-block my-2">
              <span
                className={classNames({
                  'line-through text-red-500 lg:text-orange-400 text-lg lg:text-3xl':
                    (totalPostCredits || totalPostCredits === 0) &&
                    totalPostCredits !== total,
                })}
              >
                {currency}
                {total?.toLocaleString()}
              </span>{' '}
              <span
                hidden={
                  (!totalPostCredits && totalPostCredits !== 0) ||
                  totalPostCredits === total
                }
                className="font-medium"
              >
                {currency}
                {totalPostCredits?.toLocaleString()}
              </span>{' '}
              total
            </span>
          )}
          {/* <br className="lg:hidden" /> */}
          {/* <span className="ml-2 lg:text-lg">inclusive of tax</span> */}
        </div>
        <div className="text-sm lg:text-xl">
          <p>
            Book with a deposit of{' '}
            <span
              className={classNames({
                hidden:
                  (amountAfterCredits || amountAfterCredits === 0) &&
                  amountAfterCredits !== deposit,
              })}
            >
              {currency}
              {deposit?.toLocaleString()}
            </span>{' '}
            <span
              hidden={
                (!amountAfterCredits && amountAfterCredits !== 0) ||
                amountAfterCredits === deposit
              }
              className="font-medium"
            >
              {currency}
              {amountAfterCredits?.toLocaleString()}
            </span>
          </p>

          {!!prevDepositUsed && (
            <p>
              Previous deposit used {currency}
              {prevDepositUsed}
            </p>
          )}
          {!!credits && (
            <p>
              Credits used {currency}
              {(credits > deposit && deposit) || credits}
            </p>
          )}

          <p>
            {currency}
            {(remainder || total - deposit)?.toLocaleString()} payable on the
            day
          </p>

          {/* inclusions */}
          <div className="mt-3 lg:hidden">
            <p className="mb-2">
              <span
                className="underline cursor-pointer"
                onClick={inclusionCollapse.toggle}
              >
                {(inclusionCollapse.closed && 'Show') || 'Hide'} inclusions
              </span>
            </p>
            <ShowIf condition={inclusionCollapse.opened}>
              <ul className="pl-4 list-disc list-outside">
                {inclusions.map((inc, key) => (
                  <li className="mb-2" key={key}>
                    {inc}
                  </li>
                ))}
              </ul>
            </ShowIf>
          </div>

          <div className="hidden mt-1 mb-6 lg:block">
            <div
              className="text-lg underline cursor-pointer"
              onClick={drawer.toggle}
            >
              View inclusions & cancellation policy
            </div>
            <Drawer
              open={drawer.opened}
              onClose={drawer.toggle}
              direction="right"
              className="w-[calc(100vw-100vh)] text-white bg-black bg-opacity-75"
              zIndexClassName="z-20"
            >
              <FormWizard
                open={drawer.opened}
                title="Inclusions & Cancellation policy"
                drawerView
                hideClearBtn
                nextBtnLabel="Close"
                onNext={drawer.toggle}
                onBack={drawer.toggle}
              >
                <div className="-mr-3">
                  <ul className="hidden mb-6 list-disc list-outside lg:block lg:text-base">
                    {inclusions.map((inc, key) => (
                      <li className="mb-2" key={key}>
                        {inc}
                      </li>
                    ))}
                  </ul>
                  <div className="hidden mt-12 lg:block">
                    <div className="flex mb-3 text-2xl">
                      <FontAwesomeIcon icon={faCheck} className="mr-2" />
                      <span>COVID Cancellation Policy.</span>
                    </div>
                    <p>
                      We know plans can change rapidly, so give at least 1 day’s
                      notice and get a full credit for the deposit paid that is
                      valid for 6 months and can be used to pay the deposit on
                      any future bookings.
                    </p>
                  </div>
                </div>
              </FormWizard>
            </Drawer>
          </div>
        </div>

        <ShowIf condition={bonus}>
          <div className="mt-4 text-sm text-gray-600 lg:text-white lg:text-lg">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> {bonus}
          </div>
        </ShowIf>
      </div>

      {placeholder && (
        <div className="grid w-9/12 gap-3 mt-3 animate-pulse">
          <div className="h-3 bg-gray-200 rounded-full" />
          <div className="h-3 bg-gray-200 rounded-full" />
          <div className="h-3 bg-gray-200 rounded-full" />
          <div className="h-3 bg-gray-200 rounded-full" />
        </div>
      )}
    </div>
  );
};
export default PricingInclusions;
